import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatIST } from '@/lib/utils';
import { getProductTypeName, UNCLASSIFIED } from '@/lib/constants';
import { Progress } from '@/components/ui/progress';
import { Calendar, ChevronUp, ChevronDown, ArrowUpDown, CheckCircle2, ChevronRight, FileText, Printer, MessageSquare, Trash2 } from 'lucide-react';
import { SplitBadge, BatchSplitSummary } from '@/components/BatchSplitIndicator';
import ProductionPipeline from './ProductionPipeline';
import BatchHistoryPanel from './BatchHistoryPanel';
import { needsPlate } from '@/lib/plate';

export default function LineItemsSection({
  order,
  lineItemsSortOrder,
  toggleLineItemsSort,
  canSeeFinancials,
  canMoveStages,
  handleMoveForward,
  moveLoading,
  onMoveBack,
  isAdmin,
  onDeleteLineItem,
  expandedBatches,
  toggleBatchHistory,
}) {
  const navigate = useNavigate();

  let items = order.line_items?.filter(item => !item.is_split_child) || [];

  if (lineItemsSortOrder !== 'none') {
    items = [...items].sort((a, b) => {
      const dateA = new Date(a.created_at || order.created_at || order.order_date);
      const dateB = new Date(b.created_at || order.created_at || order.order_date);
      return lineItemsSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }

  return (
    <Card className="border-slate-100 shadow-card rounded-xl">
      <CardHeader className="py-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-sm font-semibold text-slate-600 uppercase tracking-wider">
            Items ({items.length})
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLineItemsSort}
            className={`h-7 px-2 text-xs ${lineItemsSortOrder !== 'none' ? 'text-brand-blue' : 'text-slate-400'}`}
            title={`Sort by Order Placed Date (${lineItemsSortOrder === 'none' ? 'unsorted' : lineItemsSortOrder})`}
          >
            <Calendar className="h-3.5 w-3.5 mr-1" />
            Order Placed
            {lineItemsSortOrder === 'asc' && <ChevronUp className="h-3.5 w-3.5 ml-0.5" />}
            {lineItemsSortOrder === 'desc' && <ChevronDown className="h-3.5 w-3.5 ml-0.5" />}
            {lineItemsSortOrder === 'none' && <ArrowUpDown className="h-3.5 w-3.5 ml-0.5 opacity-50" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {items.map((item, idx) => {
          const cardStatusClass = item.status === 'completed' ? 'completed' : item.status === 'in_production' ? 'in-progress' : '';
          const childItems = item.is_split ? order.line_items.filter(li => li.parent_line_item_id === item.id) : [];
          const orderPlacedDate = item.created_at || order.created_at || order.order_date;

          return (
            <div key={item.id} className={`item-card ${cardStatusClass} p-2.5`} data-testid={`item-${idx}`}>
              {/* Item header row — the placed date rides along rather than
                  taking a line of its own */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-sm font-bold text-slate-900">{item.item_name || item.name || 'Item'}</span>
                  <span className={`text-[10px] font-medium uppercase ${
                    item.order_type === UNCLASSIFIED ? 'text-brand-gold-deep' : 'text-slate-400'
                  }`}>
                    {getProductTypeName(item.order_type, true)}
                  </span>
                  <span className="text-[10px] text-slate-400" title="Order placed">
                    {formatIST(orderPlacedDate, { dateOnly: true })}
                  </span>
                  {item.expected_delivery_date && (
                    <span className="text-[10px] text-slate-500" title="Wanted by">
                      → {formatIST(item.expected_delivery_date, { dateOnly: true })}
                    </span>
                  )}
                  {!item.item_name && item.product_subtype && <span className="text-xs text-slate-400">{item.product_subtype}</span>}
                  {item.is_split && (
                    <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                      Split into {childItems.length} types
                    </Badge>
                  )}
                  {item.design_status && item.design_status !== 'not_required' && (
                    <Badge variant="outline" className={`text-[10px] ${item.design_status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      Design: {item.design_status === 'completed' ? 'Done' : item.design_status?.replace('_', ' ')}
                    </Badge>
                  )}
                  {needsPlate(item) && (
                    <Badge variant="outline" className={`text-[10px] ${
                      item.plate_status === 'received' || item.plate_received ? 'bg-green-50 text-green-700 border-green-200' :
                      item.plate_status === 'ordered' || item.plate_ordered ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {item.plate_status === 'received' || item.plate_received ? '\u2713 Plate Received' :
                       item.plate_status === 'ordered' || item.plate_ordered ? '\u23F3 Plate Ordered' : '\u26A0 Plate Required'}
                    </Badge>
                  )}
                </div>
                {/* the pills keep their own width: a squeezed row wraps them
                    whole rather than breaking "Qty: 1 • ft" across two lines */}
                <div className="flex flex-wrap items-center justify-end gap-2 whitespace-nowrap">
                  <Badge variant="outline" className="text-[10px]">Qty: {item.quantity}{item.surplus_quantity > 0 ? ` + ${item.surplus_quantity} surplus` : ''}{order.size_unit ? ` \u2022 ${order.size_unit}` : ''}</Badge>
                  {item.fulfill_from_stock && (
                    <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">From Stock</Badge>
                  )}
                  {item.is_inventory_production && (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Stock Production</Badge>
                  )}
                  {canSeeFinancials && item.item_value > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                      ₹{item.item_value.toLocaleString()}
                      {item.price_type === 'per_piece' && item.price_per_piece > 0 && (
                        <span className="ml-1 text-slate-500">(₹{item.price_per_piece}/pc)</span>
                      )}
                      {item.price_type === 'per_sheet' && item.sheets_used > 0 && (
                        <span className="ml-1 text-slate-500">({item.sheets_used} sheets)</span>
                      )}
                      {item.item_paid > 0 && ` (Paid: ₹${item.item_paid.toLocaleString()})`}
                    </Badge>
                  )}
                  {item.status && (
                    <Badge variant="outline" className={`text-[10px] ${item.status === 'completed' ? 'bg-green-50 text-green-700' : item.status === 'in_production' ? 'bg-blue-50 text-blue-700' : item.status === 'design' ? 'bg-purple-50 text-purple-700' : 'bg-slate-50 text-slate-600'}`}>
                      {item.status === 'in_production' ? 'In Production' : item.status === 'design' ? 'In Design' : item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                    </Badge>
                  )}
                  {/* the goods have gone — on signage the sign may still be going up */}
                  {item.delivery_status === 'delivered' && (
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                      Delivered
                    </Badge>
                  )}
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteLineItem(item)}
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                      title="Delete line item"
                      data-testid={`delete-item-${idx}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* The specifications are not repeated here. Every filled field
                  was tiled onto this card — eighteen of them on a five-ply
                  carton — burying the pipeline, the plate and the batches this
                  page exists to show. The job card below owns them, says how
                  many are filled, and opens straight onto them. */}

              {/* Plate Tracking Section */}
              {needsPlate(item) && (
                <div className="mb-3 p-3 rounded-lg border bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                      <Printer className="h-3.5 w-3.5" /> Plate Status
                    </p>
                    <Badge variant="outline" className={`text-[10px] ${
                      item.plate_status === 'received' || item.plate_received ? 'bg-green-50 text-green-700 border-green-200' :
                      item.plate_status === 'ordered' || item.plate_ordered ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {item.plate_status === 'received' || item.plate_received ? 'Received' :
                       item.plate_status === 'ordered' || item.plate_ordered ? 'Ordered' : 'Required \u2014 Pending'}
                    </Badge>
                  </div>
                  {(item.plate_status === 'ordered' || item.plate_status === 'received' || item.plate_ordered) && (
                    <div className="text-[10px] text-amber-700 mb-2 space-y-0.5">
                      {item.plate_ordered_at && <p>Ordered: {formatIST(item.plate_ordered_at)}</p>}
                      {item.plate_received_at && <p>Received: {formatIST(item.plate_received_at)}</p>}
                    </div>
                  )}
                  {item.plate_status !== 'received' && !item.plate_received && (
                    <Link to="/plates" className="text-[10px] text-blue-600 hover:text-blue-800 underline font-medium">
                      Manage in Plate Tracking Dashboard \u2192
                    </Link>
                  )}
                  {(item.plate_status === 'received' || item.plate_received) && (
                    <span className="text-[10px] text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Plate ready for printing</span>
                  )}
                  {(() => {
                    const activeBatch = item.batches?.find(b => b.status === 'in_progress');
                    const stageKey = activeBatch && item.production_stages?.[activeBatch.current_stage_index]?.stage_key;
                    const isPrintingStage = stageKey?.endsWith('_printing') || stageKey === 'plate_making';
                    return isPrintingStage && item.plate_status !== 'received' && !item.plate_received;
                  })() && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-[10px] text-red-700 font-medium">
                      \u26A0 Plate not received \u2014 printing stage is BLOCKED until plate is marked as received.
                    </div>
                  )}
                </div>
              )}

              {/* An item booked without a product type has no pipeline yet — its
                  job card is what decides how it is made, and until that is
                  filled in the item cannot move. */}
              {item.job_card_id && item.order_type === UNCLASSIFIED && (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 my-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-amber-600" />
                    <div>
                      <p className="text-xs font-semibold text-amber-900">Job card not filled in</p>
                      <p className="text-[10px] text-amber-700">
                        Pick the order type and the processes it runs to start production.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="h-7 bg-brand-gold text-xs text-brand-navy hover:bg-brand-gold-dark"
                    onClick={() => navigate(`/job-card/${item.job_card_id}/fill`)}
                    data-testid={`fill-job-card-${idx}`}
                  >
                    Fill Job Card <ChevronRight className="ml-0.5 h-3 w-3" />
                  </Button>
                </div>
              )}

              {/* Job Card Link */}
              {item.job_card_id && item.order_type !== UNCLASSIFIED && (
                <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg px-3 py-2.5 my-3 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-brand-blue" />
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Job Card</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Progress value={item.job_card_completion?.completion_percentage || 0} className="h-1.5 w-24" />
                        <span className="text-[10px] text-slate-500">
                          {item.job_card_completion?.filled_fields || 0}/{item.job_card_completion?.total_fields || 0} fields
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-slate-500"
                      onClick={() => navigate(`/job-card/${item.job_card_id}/fill`)}
                    >
                      Fill
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-7 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"
                      onClick={() => navigate(`/job-card/${item.job_card_id}`)}
                    >
                      Open <ChevronRight className="h-3 w-3 ml-0.5" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Production Pipeline + Batches */}
              {item.production_stages?.length > 0 && !item.is_split && (
                <div className="mt-3 pt-3 border-t">
                  <ProductionPipeline stages={item.production_stages} batches={item.batches} />

                  {item.batches && item.batches.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Batches</p>
                        <BatchSplitSummary batches={item.batches} />
                      </div>
                      {item.batches.map((batch) => {
                        const atFirstStage = (batch.current_stage_index || 0) <= 0;
                        const atLastStage = (batch.current_stage_index || 0) >= (item.production_stages?.length || 1) - 1;
                        const prevStageName = !atFirstStage ? item.production_stages?.[batch.current_stage_index - 1]?.stage_name : null;
                        const nextStageName = !atLastStage ? item.production_stages?.[(batch.current_stage_index || 0) + 1]?.stage_name : null;
                        const isExpanded = expandedBatches[batch.id];
                        const hasHistory = batch.stage_history?.length > 0;
                        const hasRemarks = batch.stage_history?.some(h => h.remarks && h.remarks.trim());
                        const hasSplitInfo = batch.split_from || batch.has_splits || batch.child_batch_ids?.length > 0;
                        return (
                          <div key={batch.id} className="rounded-lg border bg-white overflow-hidden">
                            {/* Batch Header Row */}
                            <div
                              className={`flex items-center gap-2 text-xs p-2 flex-wrap ${hasHistory || hasSplitInfo ? 'cursor-pointer hover:bg-slate-50 transition-colors' : ''}`}
                              onClick={() => (hasHistory || hasSplitInfo) && toggleBatchHistory(batch.id)}
                            >
                              {(hasHistory || hasSplitInfo) && (
                                <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              )}
                              <span className="font-semibold text-slate-700">#{batch.batch_number}</span>
                              <span className="text-slate-400">Qty: {batch.quantity?.toLocaleString()}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                batch.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' :
                                batch.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                'bg-slate-50 text-slate-500 border border-slate-200'
                              }`}>
                                {batch.status === 'completed' ? 'Done' : batch.current_stage_name}
                              </span>
                              <SplitBadge batch={batch} />
                              {batch.revisited_stages?.length > 0 && (
                                <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded border border-amber-200">revisited</span>
                              )}
                              {hasHistory && (
                                <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                  {batch.stage_history.length} stages done
                                  {hasRemarks && <MessageSquare className="h-3 w-3 text-amber-400 ml-0.5" />}
                                </span>
                              )}
                              {canMoveStages && batch.status !== 'completed' && (
                                <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
                                  {!atFirstStage && (
                                    <button
                                      className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 text-xs font-medium transition-colors"
                                      title={`Move back to ${prevStageName}`}
                                      disabled={moveLoading}
                                      onClick={() => onMoveBack({
                                        jobCardId: item.job_card_id,
                                        batchId: batch.batch_id || batch.id,
                                        fromStage: batch.current_stage_name,
                                        toStage: prevStageName,
                                      })}
                                    >
                                      <ChevronUp className="h-3.5 w-3.5" />
                                      <span>Back to {prevStageName}</span>
                                    </button>
                                  )}
                                  {!atLastStage && (
                                    <button
                                      className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-green-50 hover:bg-green-100 text-green-700 border border-green-300 text-xs font-medium transition-colors"
                                      title={`Move to ${nextStageName}`}
                                      disabled={moveLoading}
                                      onClick={() => handleMoveForward(item.job_card_id, batch.batch_id || batch.id)}
                                    >
                                      <span>Move to {nextStageName}</span>
                                      <ChevronDown className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>

                            {isExpanded && <BatchHistoryPanel batch={batch} />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Split Types Sub-cards */}
              {item.is_split && childItems.length > 0 && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2">Types</p>
                  {childItems.map(child => (
                    <div key={child.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {child.type_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{child.type_name || child.name}</p>
                          <p className="text-[10px] text-slate-500">Qty: {child.quantity?.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {child.status && (
                          <Badge variant="outline" className={`text-[10px] ${child.status === 'completed' ? 'bg-green-50 text-green-700' : child.status === 'in_production' ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-500'}`}>
                            {child.status === 'in_production' ? 'In Production' : child.status?.charAt(0).toUpperCase() + child.status?.slice(1)}
                          </Badge>
                        )}
                        {child.job_card_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[10px] h-6 px-2 text-blue-600"
                            onClick={() => navigate(`/job-card/${child.job_card_id}`)}
                          >
                            Job Card <ChevronRight className="h-3 w-3 ml-0.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
